/**
 * Auth form schemas — UI-SPEC §Form Validation Contract.
 *
 * These are UI-layer schemas that drive React Hook Form + Zod validation on
 * the auth screens (login, OTP entry, password reset). Per project conventions:
 *   - If an endpoint exists, prefer a schema from `@linkhr/api-client`.
 *   - UI-only validation goes in `@linkhr/hr-domain` OR (Phase 1 specific)
 *     in `@linkhr/auth/schemas` because auth schemas are co-located with the
 *     shared auth UI components that consume them.
 *
 * Zod v3 is the pinned version across all three apps (blocked from zod v4
 * by `@t3-oss/env-core@^0.11`'s peerDep). Schemas use v3-compatible API —
 * `.email()` / `.min()` / `.refine()` are identical in both majors.
 *
 * Error strings are i18n keys (not literal user-facing text) so React Hook
 * Form's `fieldState.error.message` can be passed through `t(...)` at the
 * render site.
 */
import { z } from "zod";

/** Login — tenant admin / employee. Used by both hr-admin + employee-web.
 *  `rememberMe` (CC-87 / be-094): true → 30-day refresh-token TTL (default),
 *  false → 7-day TTL. Backend treats absent / null / true as the long TTL,
 *  so we keep `true` as the default state. */
export const loginSchema = z.object({
  // Email OR username (`<PREFIX>-<employee number>`). No `.email()` and no
  // lower-casing: the backend matches the username's number exactly.
  identifier: z.string().trim().min(1, "validation.required"),
  password: z.string().min(1, "validation.required"),
  rememberMe: z.boolean(),
});
export type LoginSchema = z.infer<typeof loginSchema>;

/**
 * Platform-staff login — email + password only. NO `rememberMe`: linkco ops
 * don't get long-lived sessions on the platform admin surface (UI-SPEC §2).
 *
 * Critical: do not alias `loginSchema` here. The tenant schema requires
 * `rememberMe: z.boolean()` (NOT optional, NOT defaulted). The platform
 * form (`<PlatformLoginFormInline>`) renders no remember-me field and only
 * seeds `{ email, password }` into RHF's defaultValues — so reusing the
 * tenant schema makes Zod reject every submission on the missing
 * `rememberMe` field, RHF silently blocks the submit (no toast, no
 * network), and the user sees the click do nothing.
 */
export const platformLoginSchema = z.object({
  email: z.string().min(1, "validation.required").email("validation.email.invalid"),
  password: z.string().min(1, "validation.required"),
});
export type PlatformLoginSchema = z.infer<typeof platformLoginSchema>;

/**
 * Password-reset request — email or username. Back-end resolves 202 always.
 * A username has no inbox to send a link to; the form tells the person to ask
 * their administrator instead of calling the API.
 */
export const resetRequestSchema = z.object({
  identifier: z.string().trim().min(1, "validation.required"),
});
export type ResetRequestSchema = z.infer<typeof resetRequestSchema>;

/**
 * Password-reset complete — new password + confirmation with cross-field
 * equality check. The `.refine` attaches the mismatch message to
 * `confirmPassword` so the error renders next to the confirmation field.
 */
export const resetCompleteSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "validation.password.too_short")
      .max(128, "validation.password.too_long"),
    confirmPassword: z.string().min(1, "validation.required"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "auth.reset.error.password_mismatch",
  });
export type ResetCompleteSchema = z.infer<typeof resetCompleteSchema>;

/**
 * Signed-in password change — the reset form's exact new-password rules
 * (8–128, typed twice) plus the current password the backend re-checks.
 */
export const changePasswordSchema = resetCompleteSchema
  .innerType()
  .extend({ currentPassword: z.string().min(1, "validation.required") })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "auth.reset.error.password_mismatch",
  });
export type ChangePasswordSchema = z.infer<typeof changePasswordSchema>;

/**
 * Invite-accept form (EMP-05 / CC-52). Password + confirmation only.
 *
 * NO email field: the invitee's address is already known from the invite
 * token, fetched read-only via `GET /auth/invite/{token}/preview` (CC-56) and
 * forwarded into the chained step-2 login. The user only chooses a password.
 *
 * Password rule is length-only, min 8 chars — the single requirement the
 * backend `PasswordPolicy` enforces since CC-155 (complexity rules removed).
 * No uppercase/digit/symbol checks. The backend stays the authority and
 * rejects too-short passwords with `AUTH_WEAK_PASSWORD`; this soft check just
 * gives early feedback with the same threshold. `confirmPassword` is a typo
 * guard on a masked field, not a second requirement.
 */
export const inviteAcceptSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "validation.password.too_short")
      .max(128, "validation.password.too_long"),
    confirmPassword: z.string().min(1, "validation.required"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "auth.invite.error.password_mismatch",
  });
export type InviteAcceptSchema = z.infer<typeof inviteAcceptSchema>;

/** OTP — exactly 6 digits. Uses `regex` to block non-numeric input. */
export const otpSchema = z.object({
  code: z
    .string()
    .length(6, "auth.otp.error.format")
    .regex(/^\d{6}$/, "auth.otp.error.format"),
});
export type OtpSchema = z.infer<typeof otpSchema>;

/**
 * TOTP challenge — accepts EITHER a 6-digit authenticator code OR a recovery
 * code (6–16 alphanumeric chars, hyphens allowed). One permissive schema covers
 * both input modes; the form's "use a recovery code" toggle only changes the
 * input hint / length affordance. The backend stays the authority on validity.
 */
export const totpSchema = z.object({
  code: z
    .string()
    .trim()
    .refine((v) => /^\d{6}$/.test(v) || /^[A-Za-z0-9-]{6,16}$/.test(v), "auth.totp.error.format"),
});
export type TotpSchema = z.infer<typeof totpSchema>;
